import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import config from "../../../config";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const WPEditCredentials = () => {
  const { store_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [testSuccess, setTestSuccess] = useState(null);

  const [formData, setFormData] = useState({
    storeName: "",
    posSystem: "WordPress",
    storeUrl: "",
    consumerKey: "",
    consumerSecret: ""
  });

  useEffect(() => {
    const loadStoreConfig = async () => {
      if (!store_id) return;
      try {
        setInitialLoading(true);
        const response = await fetch(`${config.API_BASE_URL}/client/pos/get-store-config/${store_id}`, {
          method: "GET",
          headers: config.getHeaders()
        });

        if (!response.ok) throw new Error("Failed to load store configuration");

        const result = await response.json();
        if (result.success && result.data) {
          const conf = result.data;
          
          let secret = "";
          if (conf.wc_config) {
            try {
              const parsed = JSON.parse(conf.wc_config);
              secret = parsed.consumer_secret || "";
            } catch(e) {}
          }

          setFormData({
            storeName: conf.store_name || "",
            posSystem: conf.pos_name || "WordPress",
            storeUrl: conf.pos_url || "",
            consumerKey: conf.pos_api_key || "",
            consumerSecret: secret
          });
        }
      } catch (err) {
        console.error("Load config error:", err);
        setError("Failed to load store credentials");
      } finally {
        setInitialLoading(false);
      }
    };
    loadStoreConfig();
  }, [store_id]);

  const handleTestConnection = async () => {
    setTestLoading(true);
    setError(null);
    setTestSuccess(null);
    setSuccess(null);

    try {
      const response = await fetch(`${config.API_BASE_URL}/client/pos/wordpress/test`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({
          client_store_id: store_id,
          store_url: formData.storeUrl,
          consumer_key: formData.consumerKey,
          consumer_secret: formData.consumerSecret
        })
      });

      const result = await response.json();
      if (result.success) {
        setTestSuccess(result.message || "Connection successful!");
      } else {
        setError(result.message || "Connection failed.");
      }
    } catch (err) {
      setError("Failed to test connection.");
    } finally {
      setTestLoading(false);
    }
  };

  const handleUpdateCredentials = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setTestSuccess(null);

    try {
      const response = await fetch(`${config.API_BASE_URL}/client/pos/wordpress/update-credentials`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({
          client_store_id: store_id,
          store_url: formData.storeUrl,
          consumer_key: formData.consumerKey,
          consumer_secret: formData.consumerSecret
        })
      });

      const result = await response.json();
      if (result.success) {
        setSuccess("Credentials updated successfully!");
        setTimeout(() => navigate("/pos-sync"), 2000);
      } else {
        setError(result.message || "Failed to update credentials.");
      }
    } catch (err) {
      setError("An error occurred while updating credentials.");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Card sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Card>
    );
  }

  return (
    <Card sx={{ p: 4, maxWidth: 600, mx: "auto", mt: 4, borderRadius: 3, boxShadow: "0 10px 40px -10px rgba(0,0,0,0.1)" }}>
      <Typography variant="h5" fontWeight="bold" mb={1}>
        Update API Credentials
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Update the WooCommerce API keys for <strong>{formData.storeName}</strong>.
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {testSuccess && <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleOutlineIcon />}>{testSuccess}</Alert>}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Store URL"
          fullWidth
          value={formData.storeUrl}
          onChange={(e) => setFormData({ ...formData, storeUrl: e.target.value })}
          placeholder="https://yourstore.com"
        />
        
        <TextField
          label="Consumer Key (cs_...)"
          fullWidth
          value={formData.consumerKey}
          onChange={(e) => setFormData({ ...formData, consumerKey: e.target.value })}
        />

        <TextField
          label="Consumer Secret (ck_...)"
          fullWidth
          type="password"
          value={formData.consumerSecret}
          onChange={(e) => setFormData({ ...formData, consumerSecret: e.target.value })}
        />

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button 
            variant="outlined" 
            fullWidth 
            onClick={handleTestConnection}
            disabled={testLoading || loading || !formData.consumerKey || !formData.consumerSecret || !formData.storeUrl}
          >
            {testLoading ? <CircularProgress size={24} /> : "Test Connection"}
          </Button>

          <Button 
            variant="contained" 
            fullWidth 
            onClick={handleUpdateCredentials}
            disabled={loading || testLoading || !formData.consumerKey || !formData.consumerSecret || !formData.storeUrl}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Save Credentials"}
          </Button>
        </Box>
        <Button variant="text" color="inherit" onClick={() => navigate("/pos-sync")}>
          Cancel
        </Button>
      </Box>
    </Card>
  );
};

export default WPEditCredentials;
